<template>
  <div
    class="feature-explainer"
    :class="`feature-explainer--${type}`"
    role="dialog"
    aria-modal="true"
    :aria-label="content.title"
    @click.self="$emit('close')"
  >
    <div class="explainer__content">
      <!-- Animated stairs illustration -->
      <svg class="explainer__art" viewBox="0 0 140 100" aria-hidden="true">
        <!-- Ascending stairs (RTL: rise towards the left) -->
        <g class="art-stairs">
          <rect class="art-stair art-stair--1" x="96" y="64" width="32" height="26" rx="4" />
          <rect class="art-stair art-stair--2" x="56" y="42" width="32" height="48" rx="4" />
          <rect class="art-stair art-stair--3" x="16" y="20" width="32" height="70" rx="4" />
        </g>
        <!-- Rising arrow above the stairs -->
        <path class="art-arrow" d="M116 52 L72 28 L36 8" fill="none" />
        <path class="art-arrow-head" d="M44 6 L36 8 L38 16" fill="none" />
        <!-- Sparkle -->
        <g class="art-spark">
          <path d="M124 14 l2 4.4 4.4 2 -4.4 2 -2 4.4 -2 -4.4 -4.4 -2 4.4 -2 Z" />
        </g>
      </svg>

      <span class="explainer__chip">{{ content.chip }}</span>
      <h2 class="explainer__title">{{ content.title }}</h2>
      <p class="explainer__subtitle">{{ content.subtitle }}</p>

      <!-- The item's actual ladder -->
      <div v-if="tiers.length" class="explainer__tiers">
        <div
          v-for="(tier, i) in visibleTiers"
          :key="i"
          class="explainer__tier"
          :style="{ '--tier-delay': `${0.35 + i * 0.15}s` }"
        >
          <span class="tier-qty">{{ tier.qty }}</span>
          <svg
            class="tier-arrow"
            viewBox="0 0 24 24"
            width="14"
            height="14"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
          <span class="tier-value">{{ tier.value }}</span>
        </div>
      </div>

      <!-- How it works -->
      <ul class="explainer__points">
        <li v-for="(point, i) in content.points" :key="i">
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <span>{{ point }}</span>
        </li>
      </ul>

      <button type="button" class="explainer__cta" @click="$emit('close')">
        הבנתי, בואו נתחיל
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

/**
 * Fullscreen one-time explainer for pricing mechanisms, shown the first time
 * a user opens an item dialog with a madregot promotion or a quantity discount.
 * `tiers` carries the item's actual ladder: [{ qty, value }]
 */
const props = defineProps({
  type: {
    type: String,
    required: true,
    validator: (v) => v === 'madregot' || v === 'discount',
  },
  tiers: {
    type: Array,
    default: () => [],
  },
})

defineEmits(['close'])

const CONTENT = {
  madregot: {
    chip: 'מבצע מדרגות',
    title: 'המחיר יורד ככל שקונים יותר',
    subtitle: 'לפריט הזה יש מבצע מדרגות — ככה זה עובד:',
    points: [
      'המחיר ליחידה נקבע לפי הכמות הכוללת שבעגלה',
      'הכמות נספרת במצטבר על כל המוצרים שמשתתפים במבצע',
      'עוברים מדרגה? המחיר המשתלם חל על היחידות שבמבצע',
    ],
  },
  discount: {
    chip: 'הנחת כמות',
    title: 'קונים יותר, משלמים פחות',
    subtitle: 'לפריט הזה יש הנחת כמות — ככה זה עובד:',
    points: [
      'אחוז ההנחה גדל ככל שמוסיפים יותר יחידות',
      'הכמות נספרת יחד על כל המוצרים מאותה קבוצה',
      'ההנחה חלה על כל היחידות בקבוצה — לא רק על האחרונות',
    ],
  },
}

const content = computed(() => CONTENT[props.type])

// Keep the slide compact even for long ladders
const visibleTiers = computed(() => props.tiers.slice(0, 4))
</script>

<style scoped>
.feature-explainer {
  position: fixed;
  inset: 0;
  z-index: 10010;
  display: flex;
  align-items: center;
  justify-content: center;
  direction: rtl;
  font-family: var(--font-family);
  background: linear-gradient(160deg, #0f172a 0%, #1e293b 55%, #334155 100%);
  overflow-y: auto;
  -webkit-tap-highlight-color: transparent;
  /* Accent per mechanism */
  --accent: #fbbf24;
  --accent-strong: #f59e0b;
  --accent-soft: rgba(251, 191, 36, 0.14);
}

.feature-explainer--discount {
  --accent: #34d399;
  --accent-strong: #10b981;
  --accent-soft: rgba(16, 185, 129, 0.14);
}

.explainer__content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 440px;
  width: 100%;
  padding: var(--spacing-xl) var(--spacing-lg);
  animation: explainerIn 0.45s cubic-bezier(0.22, 1, 0.36, 1) both;
}

@keyframes explainerIn {
  from {
    opacity: 0;
    transform: translateY(16px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* ---------- Illustration ---------- */
.explainer__art {
  width: clamp(110px, 26vw, 150px);
  margin-bottom: var(--spacing-md);
}

.art-stair {
  fill: var(--accent-soft);
  stroke: var(--accent);
  stroke-width: 2;
  transform-box: fill-box;
  transform-origin: bottom center;
  animation: stairRise 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}

.art-stair--1 {
  animation-delay: 0.15s;
}

.art-stair--2 {
  animation-delay: 0.3s;
}

.art-stair--3 {
  animation-delay: 0.45s;
}

@keyframes stairRise {
  from {
    opacity: 0;
    transform: scaleY(0.2);
  }
  to {
    opacity: 1;
    transform: scaleY(1);
  }
}

.art-arrow,
.art-arrow-head {
  stroke: #e2e8f0;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: 120;
  stroke-dashoffset: 120;
  animation: arrowDraw 0.6s ease-out 0.65s forwards;
}

@keyframes arrowDraw {
  to {
    stroke-dashoffset: 0;
  }
}

.art-spark {
  fill: var(--accent);
  opacity: 0;
  transform-box: fill-box;
  transform-origin: center;
  animation: sparkTwinkle 1.6s ease-in-out 1s infinite;
}

@keyframes sparkTwinkle {
  0%,
  100% {
    opacity: 0;
    transform: scale(0.6);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
}

/* ---------- Text ---------- */
.explainer__chip {
  padding: 4px 14px;
  border-radius: var(--border-radius-full);
  background: var(--accent-soft);
  border: 1px solid var(--accent);
  color: var(--accent);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  margin-bottom: var(--spacing-sm);
}

.explainer__title {
  margin: 0 0 var(--spacing-xs);
  color: var(--color-white);
  font-size: clamp(1.3rem, 4.5vw, 1.6rem);
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--letter-spacing-tight);
}

.explainer__subtitle {
  margin: 0 0 var(--spacing-md);
  color: var(--color-gray-300);
  font-size: clamp(0.9rem, 3vw, 1rem);
}

/* ---------- Real tier ladder ---------- */
.explainer__tiers {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  max-width: 300px;
  margin-bottom: var(--spacing-md);
}

.explainer__tier {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-sm);
  padding: 8px 14px;
  border-radius: var(--border-radius);
  background: rgba(248, 250, 252, 0.06);
  border: 1px solid rgba(248, 250, 252, 0.12);
  animation: tierIn 0.4s ease-out var(--tier-delay) both;
}

@keyframes tierIn {
  from {
    opacity: 0;
    transform: translateX(12px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.tier-qty {
  color: var(--color-gray-200);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
}

.tier-arrow {
  color: var(--color-gray-500);
  flex-shrink: 0;
}

.tier-value {
  color: var(--accent);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  font-variant-numeric: tabular-nums;
}

/* ---------- Points ---------- */
.explainer__points {
  list-style: none;
  margin: 0 0 var(--spacing-lg);
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  text-align: start;
}

.explainer__points li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  color: var(--color-gray-300);
  font-size: var(--font-size-sm);
  line-height: 1.5;
}

.explainer__points svg {
  flex-shrink: 0;
  margin-top: 2px;
  color: var(--accent);
}

/* ---------- CTA ---------- */
.explainer__cta {
  min-height: 48px;
  padding: 12px 36px;
  border: none;
  border-radius: var(--border-radius-full);
  background: linear-gradient(135deg, var(--accent), var(--accent-strong));
  color: #0f172a;
  font-family: inherit;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  transition: all var(--transition-fast);
  -webkit-tap-highlight-color: transparent;
}

.explainer__cta:hover {
  filter: brightness(1.08);
  transform: translateY(-1px);
}

.explainer__cta:active {
  transform: translateY(0) scale(0.98);
}

/* ---------- Reduced motion ---------- */
@media (prefers-reduced-motion: reduce) {
  .explainer__content,
  .art-stair,
  .art-arrow,
  .art-arrow-head,
  .art-spark,
  .explainer__tier {
    animation: none;
  }

  .art-arrow,
  .art-arrow-head {
    stroke-dashoffset: 0;
  }

  .art-spark {
    opacity: 1;
  }
}
</style>
